import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
import { ContactBand } from '../components/ContactBand';
import { capabilities, domains } from '../data/capabilities';
import { scenarios } from '../data/scenarios';
import { management, advisors } from '../data/people';
import { papers } from '../data/papers';
export function Home() {
  const reduceMotion = useReducedMotion();
  const enter = (delay: number) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.28,
      delay,
      ease: [0.23, 1, 0.32, 1] as const
    }
  };
  return <>
      {/* Hero */}
      <section className="border-b border-ink-200 bg-paper-raised" data-id="element-142" data-mp="3kvae4s3vmly6">
        <div className="mx-auto max-w-6xl px-6 pb-20 pt-20 lg:px-10 lg:pb-28 lg:pt-28" data-id="element-143" data-mp="3kv0pp8yhcmul">
          <motion.p {...enter(0)} className="text-[11px] uppercase tracking-label text-accent" data-id="element-144" data-mp="3ovasyduw0z5r">
            Advanced software development &amp; applied research
          </motion.p>
          <motion.h1 {...enter(0.05)} className="mt-6 max-w-4xl font-display text-[42px] leading-[1.05] tracking-[-0.015em] text-ink-950 sm:text-[64px] lg:text-[76px]" data-id="element-145" data-mp="2vvlu2bz3hy2u">
            Software problems that resist the obvious solution.
          </motion.h1>
          <motion.div {...enter(0.1)} className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_1fr]" data-id="element-146" data-mp="2hz8fpbpuycsl">
            <p className="max-w-prose text-[18px] leading-relaxed text-ink-700" data-id="element-147" data-mp="2d5w1zwmpvqem">
              Research Science Group enables developers, teams, and organizations to understand and
              take advantage of changing technology. We work in unison with your project teams to
              educate, recommend, execute, and deliver — so that your teams succeed where others
              fail.
            </p>
            <div className="flex flex-wrap items-start gap-x-6 gap-y-4 lg:justify-end lg:pt-1" data-id="element-148" data-mp="1uafotto3ev4u">
              <Link to="/contact" className="group inline-flex items-center gap-2 bg-ink-950 px-5 py-3 text-sm text-paper transition-colors duration-150 ease-out hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2" data-id="element-149" data-mp="1kmtm6ozo3hvc">
                Start a conversation
                <ArrowRightIcon aria-hidden="true" className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" data-id="element-150" data-mp="23c9nvfn39gdc" />
              </Link>
              <Link to="/services" className="inline-flex items-center gap-2 py-3 text-sm text-ink-950 underline decoration-ink-300 decoration-1 underline-offset-4 transition-colors duration-150 ease-out hover:text-accent hover:decoration-accent" data-id="element-151" data-mp="1tklzxopqqhgm">
                See what we do
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services summary */}
      <section className="mx-auto max-w-6xl px-6 py-20 lg:px-10" aria-labelledby="home-services" data-id="element-152" data-mp="amqv9e94nq7q">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.6fr] lg:gap-20" data-id="element-153" data-mp="oe4bojbqo7yj">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-154" data-mp="og8eexxwj0x8">
            <p className="text-[11px] uppercase tracking-label text-ink-500" data-id="element-155" data-mp="kdlk8qdrd3wv">01 — Services</p>
            <h2 id="home-services" className="mt-4 font-display text-[32px] leading-tight text-ink-950" data-id="element-156" data-mp="3stdmv7bcf9id">
              For innovators pushing the known limits of software technology
            </h2>
            <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-ink-500" data-id="element-157" data-mp="5ljmpcz5un00">
              On-site or off-site development across desktop, mobile, web, server, cloud, and
              embedded systems — plus the research that makes hard parts tractable.
            </p>
            <Link to="/services" className="mt-6 inline-flex items-center gap-1.5 text-sm text-accent underline decoration-accent/40 decoration-1 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-accent" data-id="element-158" data-mp="1c36tbr6di0t1">
              All services and scenarios
              <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-159" data-mp="2harij584l9b3" />
            </Link>
          </div>

          <ol className="border-t border-ink-200" data-id="element-160" data-mp="2m92sdfyf7jtb">
            {capabilities.map((capability, index) => <li key={capability.title} className="grid gap-1.5 border-b border-ink-200 py-6 sm:grid-cols-[3rem_1fr] sm:gap-6" data-id="element-161" data-mp="2qawnl8pujvm3" data-mpi={index}>
                <span aria-hidden="true" className="font-display text-sm text-accent" data-id="element-162" data-mp="303c6c88ywo81">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div data-id="element-163" data-mp="3a1rnpji70j4c">
                  <h3 className="font-display text-[19px] leading-snug text-ink-950" data-id="element-164" data-mp="3a2b8d7z89lm2">
                    {capability.title}
                  </h3>
                  <p className="mt-1.5 max-w-prose text-[15px] leading-relaxed text-ink-500" data-id="element-165" data-mp="12eq9upqrixw1">
                    {capability.description}
                  </p>
                </div>
              </li>)}
          </ol>
        </div>

        <div className="mt-14 border-t border-ink-200 pt-8" data-id="element-166" data-mp="tf36hyeh3i0c">
          <p className="text-[11px] uppercase tracking-label text-ink-500" data-id="element-167" data-mp="ofgv7ui7gbu6">Customer domains</p>
          <p className="mt-4 max-w-4xl font-display text-[19px] leading-relaxed text-ink-900" data-id="element-168" data-mp="1m2pjfx9lbyzv">
            {domains.join(' · ')}
          </p>
        </div>
      </section>

      {/* Scenarios teaser */}
      <section className="border-y border-ink-200 bg-ink-950" aria-labelledby="home-scenarios" data-id="element-169" data-mp="1ccw6osxppdu1">
        <div className="mx-auto max-w-6xl px-6 py-20 lg:px-10" data-id="element-170" data-mp="17ecj6o3gztzk">
          <div className="max-w-2xl" data-id="element-171" data-mp="17bytyvvh1bzx">
            <p className="text-[11px] uppercase tracking-label text-accent-bright" data-id="element-172" data-mp="24tyvad6u2zum">
              Where teams get stuck
            </p>
            <h2 id="home-scenarios" className="mt-4 font-display text-[32px] leading-tight text-paper sm:text-[40px]" data-id="element-173" data-mp="2015q5o7gdpi0">
              Four situations we are usually called into
            </h2>
          </div>

          <ul className="mt-12 grid gap-x-16 gap-y-0 border-t border-white/15 sm:grid-cols-2" data-id="element-174" data-mp="3kl13347fuugi">
            {scenarios.map(scenario => <li key={scenario.id} className="border-b border-white/15" data-id="element-175" data-mp="3piksn09l6tnr">
                <Link to={`/services#${scenario.id}`} className="group flex items-start gap-5 py-7 focus:outline-none focus-visible:ring-2 focus-visible:ring-paper" data-id="element-176" data-mp="3am3fq4lr16z3">
                  <span aria-hidden="true" className="font-display text-sm text-accent-bright" data-id="element-177" data-mp="31n8c90a3aazh">
                    {scenario.number}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-178" data-mp="zutkl9hqts5v">
                    <span className="block font-display text-[22px] leading-snug text-paper transition-colors duration-150 ease-out group-hover:text-accent-bright" data-id="element-179" data-mp="zv3akx2sfzbk">
                      {scenario.title}
                    </span>
                    <span className="mt-1.5 block text-[15px] leading-relaxed text-ink-300" data-id="element-180" data-mp="1ev7do0b6vzi8">
                      {scenario.summary}
                    </span>
                  </span>
                  <ArrowUpRightIcon aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-ink-500 transition-colors duration-150 ease-out group-hover:text-paper" data-id="element-181" data-mp="1nrzxr3g7c0m3" />
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Research + People */}
      <section className="mx-auto max-w-6xl px-6 py-20 lg:px-10" data-id="element-182" data-mp="26p16mzcvmkvb">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24" data-id="element-183" data-mp="22n64jsn4g3gh">
          <div aria-labelledby="home-research" data-id="element-184" data-mp="22nxq0an08kt1">
            <p className="text-[11px] uppercase tracking-label text-ink-500" data-id="element-185" data-mp="ca6x52f8yo7t">02 — Research</p>
            <h2 id="home-research" className="mt-4 font-display text-[30px] leading-tight text-ink-950" data-id="element-186" data-mp="89eoaaaaelc2">
              Most of it stays unpublished
            </h2>
            <p className="mt-4 max-w-prose text-[16px] leading-relaxed text-ink-700" data-id="element-187" data-mp="11smq0qj1vxyn">
              The considerable majority of our research remains unpublished — to protect proprietary
              trade secrets, and because writing papers competes directly with pursuing R&amp;D. Our
              published work concentrates on Artificial General Intelligence and virtual agent
              control.
            </p>
            <ul className="mt-8 border-t border-ink-200" data-id="element-188" data-mp="ivbqd4tib2io">
              {papers.slice(0, 3).map(paper => <li key={paper.title} className="border-b border-ink-200 py-4" data-id="element-189" data-mp="mv419o6dhkr7">
                  <p className="font-display text-[17px] leading-snug text-ink-950" data-id="element-190" data-mp="rw1l05716mut">
                    {paper.title}
                  </p>
                  <p className="mt-1 text-sm text-ink-500" data-id="element-191" data-mp="1yd57qx519276">
                    {paper.venue} · {paper.year}
                  </p>
                </li>)}
            </ul>
            <Link to="/research" className="mt-6 inline-flex items-center gap-1.5 text-sm text-accent underline decoration-accent/40 decoration-1 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-accent" data-id="element-192" data-mp="2ddkilwycizkd">
              Read the research page
              <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-193" data-mp="1zmfdwgg4fh36" />
            </Link>
          </div>

          <div aria-labelledby="home-people" data-id="element-194" data-mp="1ulhvq3v42n1o">
            <p className="text-[11px] uppercase tracking-label text-ink-500" data-id="element-195" data-mp="1po87usndejfy">03 — People</p>
            <h2 id="home-people" className="mt-4 font-display text-[30px] leading-tight text-ink-950" data-id="element-196" data-mp="1koverq5wog4x">
              A small group with unusually deep range
            </h2>
            <p className="mt-4 max-w-prose text-[16px] leading-relaxed text-ink-700" data-id="element-197" data-mp="3f2ei5gmmasy5">
              Engagements are staffed by principals. The person who scopes your problem is the person
              who works on it.
            </p>
            <ul className="mt-8 border-t border-ink-200" data-id="element-198" data-mp="cqo83xouamzb">
              {[...management, ...advisors].map(person => <li key={person.name} className="flex items-baseline justify-between gap-6 border-b border-ink-200 py-4" data-id="element-199" data-mp="mmr74uoea5i1">
                  <span className="font-display text-[17px] text-ink-950" data-id="element-200" data-mp="hllesxynck91">
                    {person.name}
                    {person.credential && <span className="text-ink-500" data-id="element-201" data-mp="3l4ohyx8k5e8d">, {person.credential}</span>}
                  </span>
                  <span className="text-right text-sm text-ink-500" data-id="element-202" data-mp="3nn545v9tbzv">{person.role}</span>
                </li>)}
            </ul>
            <Link to="/people" className="mt-6 inline-flex items-center gap-1.5 text-sm text-accent underline decoration-accent/40 decoration-1 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-accent" data-id="element-203" data-mp="3v0zunoc2863c">
              Meet the team
              <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-204" data-mp="1hubd3evkdhpk" />
            </Link>
          </div>
        </div>
      </section>

      <ContactBand eyebrow="04 — Contact" heading="If you have hard problems that need solving, let's explore how we may work together." body="Send us the shape of the problem: what you have tried, where it stalls, and what success would look like." data-id="element-205" data-mp="1mofi3sl9rfo0" />
    </>;
}